import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useListContext, useNotify, useUnselectAll, useUpdateMany, Button, Confirm, useResourceContext, useTranslate, useDataProvider } from 'react-admin';
import { useQueries } from '@tanstack/react-query';
import ResendIcon from '@/assets/icons/resend-icon.svg?react';
import RestartIcon from '@/assets/icons/restart-icon.svg?react';
import CashOutIcon from '@/assets/icons/cash-out-icon.svg?react';
import { CustomConfirmContent } from '@/components/global/buttons/CustomConfirmContent';
import { globalStyles } from '@/themes/commonStyles/global.js';
import { BulkActionsContainer } from '@/components/global/buttons/bulkButtons.jsx';

const ResendButton = ({ statusSource, dataKey, statusesToConfirm, label }) => {
    const { data, selectedIds, refetch } = useListContext();
    const [open, setOpen] = useState(false);
    const notify = useNotify();
    const translate = useTranslate();
    const resource = useResourceContext();
    const unselectAll = useUnselectAll(resource);

    const selectedData = data.filter(record => selectedIds.includes(record.id));
    const selectedStatuses = selectedData.map(record => record[statusSource]);
    const showConfirm = selectedStatuses.some(status => statusesToConfirm.includes(status));

    const [updateMany, { isLoading }] = useUpdateMany(
        resource,
        {
            ids: selectedIds,
            data: {
                ids: selectedIds,
                [dataKey]: true,
            },
        },
        {
            onSuccess: () => {
                notify(translate('resources.transaction.labels.successfullySent'));
                unselectAll();
                refetch();
            },
            onError: error => {
                notify(translate('resources.transaction.errors.resendFailed', { errorMessage: error.message }), { type: 'error' });
                refetch();
            },
        },
    );
    const handleClick = () => (showConfirm ? setOpen(true) : updateMany());
    const handleDialogClose = () => setOpen(false);
    const handleConfirm = () => {
        updateMany();
        setOpen(false);
    };

    return (
        <>
            <Button label={label} onClick={handleClick}>
                <ResendIcon />
            </Button>
            {showConfirm && (
                <Confirm
                    isOpen={open}
                    loading={isLoading}
                    content={
                        <CustomConfirmContent
                            content={translate('resources.transaction.labels.resendConfirm')}
                            handleDialogClose={handleDialogClose}
                            handleConfirm={handleConfirm}
                            loading={isLoading}
                        />
                    }
                    onConfirm={handleConfirm}
                    onClose={handleDialogClose}
                    sx={{ ...globalStyles.customConfirmDialog }}
                />
            )}
        </>
    );
};

ResendButton.propTypes = {
    statusSource: PropTypes.string.isRequired,
    dataKey: PropTypes.string.isRequired,
    statusesToConfirm: PropTypes.array.isRequired,
    label: PropTypes.string.isRequired,
};

const ChangeToNormalButton = ({ label }) => {
    const { data, selectedIds, refetch } = useListContext();
    const dataProvider = useDataProvider();
    const [open, setOpen] = useState(false);
    const notify = useNotify();
    const translate = useTranslate();
    const resource = useResourceContext();
    const unselectAll = useUnselectAll(resource);
    const [isClosedBag, setIsClosedBag] = useState(false);

    const selectedData = (data || []).filter(record => selectedIds.includes(record.id));
    const bagQueries = useQueries({
        queries: selectedData.map(record => ({
            queryKey: ['bag', record.id, record.transactionTime],
            queryFn: () =>
                dataProvider.getList('bag', {
                    filter: {
                        deviceId: record.device.id,
                        sealScanningTime_gte: record.transactionTime,
                    },
                    pagination: { page: 1, perPage: 1 },
                    sort: { field: 'id', order: 'DESC' },
                }),
            staleTime: 5 * 60 * 1000,
            enabled: !!record.device?.id && !!record.transactionTime,
        })),
    });
    const hasClosedBag = useMemo(() => {
        if (bagQueries.length === 0) {
            return false;
        }
        return bagQueries.some(q => q.data?.data?.length > 0);
    }, [bagQueries]);

    useEffect(() => {
        setIsClosedBag(hasClosedBag);
    }, [hasClosedBag]);
    const [updateMany, { isLoading }] = useUpdateMany(
        'transaction',
        {
            ids: selectedIds,
            data: {
                ids: selectedIds,
                changeToNormal: true,
            },
        },
        {
            onSuccess: () => {
                notify(translate('resources.transaction.labels.successfullyChangedToNormal'));
                unselectAll();
                refetch();
                setIsClosedBag(false);
            },
            onError: error => {
                notify(translate('resources.transaction.errors.changedToNormalFailed', { errorMessage: error.message }), { type: 'error' });
                refetch();
                setIsClosedBag(false);
            },
        },
    );
    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);
    const handleConfirm = () => {
        updateMany();
        setOpen(false);
    };

    return (
        <>
            <Button label={label} onClick={handleClick}>
                <RestartIcon />
            </Button>
            <Confirm
                isOpen={open}
                loading={isLoading}
                title={label}
                content={translate(
                    isClosedBag ? 'resources.transaction.labels.changeToNormalOldConfirm' : 'resources.transaction.labels.changeToNormalConfirm',
                )}
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
            />
        </>
    );
};

ChangeToNormalButton.propTypes = {
    label: PropTypes.string.isRequired,
};

const PrintCouponButton = () => {
    const { selectedIds, refetch } = useListContext();
    const notify = useNotify();
    const translate = useTranslate();
    const resource = useResourceContext();
    const unselectAll = useUnselectAll(resource);

    const [updateMany] = useUpdateMany(
        resource,
        {
            ids: selectedIds,
            data: {
                ids: selectedIds,
                printCoupon: true,
            },
        },
        {
            onSuccess: () => {
                notify(translate('resources.transaction.labels.successfullyPrinted'));
                unselectAll();
                refetch();
            },
            onError: error => {
                notify(translate('resources.transaction.errors.printedFailed', { errorMessage: error.message }), { type: 'error' });
                refetch();
            },
        },
    );
    const handleClick = () => updateMany();

    return (
        <Button label={translate('resources.transaction.labels.printCoupon')} onClick={handleClick}>
            <CashOutIcon />
        </Button>
    );
};

export const TransactionBulkActionButtons = () => {
    const translate = useTranslate();
    const resource = useResourceContext();
    return 'transaction' === resource ? (
        <BulkActionsContainer>
            <ResendButton statusSource='status' dataKey='resend' statusesToConfirm={[1]} label={translate('resources.transaction.labels.resend')} />
            <ResendButton
                statusSource='operatorStatus'
                dataKey='resendToOperator'
                statusesToConfirm={['sent', 'added']}
                label={translate('resources.transaction.labels.resendToOperator')}
            />
            <PrintCouponButton />
        </BulkActionsContainer>
    ) : (
        <BulkActionsContainer>
            <ChangeToNormalButton label={translate('resources.transaction.labels.changeToNormal')} />
        </BulkActionsContainer>
    );
};
